import { FetchResult, MutationResult } from "@apollo/client";
import React, {useCallback, useEffect, useState} from "react";
import { useParams} from "react-router";
import { useToast } from "../contexts/toast";

import GQLChanges from "../utils/graphql-changes";
import { DiaFormRelationships, useDiaForm} from "./dia-form";
import {useNavigate} from "react-router-dom";
import {useUnsavedChangesGuard} from "./useUnsavedChangesGuard";

let snackbarSeq = 0;
function nextSnackbarKey() {
    snackbarSeq += 1;
    return String(snackbarSeq);
}



interface YupSchema {
    validate: (any, Object) => Promise<unknown>
}


export type DiaEditorCreateRedirectFunction<MutationResultType> = (args: CreateRedirectArgs<MutationResultType>) => string;

type CreateRedirectArgs<Type> = {
    data:Type,
    baseUrl:string,
    search:string
};

export type DiaEditorSaveArgs<Type>= {
    values:Type
};

export type DiaEditorCreateArgs<Type> = {
    values:Type
};



type DiaEditorProps<Type,QueryResultType,CreateMutation,SaveMutationType,TransientType> = {
    query:QueryResultType
    save:(args:DiaEditorSaveArgs<Type>) => Promise<FetchResult<SaveMutationType>>,
    saveResult:MutationResult<any>,
    getSaveValue:(result:MutationResult<SaveMutationType>) => Type|null|undefined,
    create?:(args:DiaEditorCreateArgs<Type>) => Promise<FetchResult<CreateMutation>>,
    createResult?:MutationResult<any>|null,
    blankValues:()=>Type,
    label:string,
    saveGuard?:(values:Type,initialValues:Type)=>Promise<boolean>,
    afterSave?:(args?:any)=>void,
    baseUrl:string,
    permission:string,
    validationSchema?:YupSchema,
    transientValues?:TransientType,
    relationships?:DiaFormRelationships,
    cleanGraphqlVariables?:(variables:any,transientValues:TransientType,allValues:any)=>any,
    getValue:(query:QueryResultType)=>Type|null|undefined,
    getCreateRedirect?:DiaEditorCreateRedirectFunction<MutationResult<CreateMutation>>
    allowRedirectWhen?:(path:string,id:string)=>boolean
};

export function useDiaEditor<Type,QueryType,CreateMutationType,SaveMutationType,TransientType=never>(
        props:DiaEditorProps<Type,QueryType,CreateMutationType,SaveMutationType,TransientType>
    ): any {
    const { id } = useParams();
    const { toast } = useToast();
    const navigate = useNavigate();
    //const userContext = useUserContext();
    const waiting = (props.query as any).loading || props.saveResult.loading || props.createResult?.loading;
    const isNew = (id === 'new');
    const formSubmit = useCallback(async ({ values, initialValues, hasFailed, hasCompleted,transientValues,setTransient }) => {
        const changes = new GQLChanges(props.relationships);
        let variables = changes.compare(initialValues, values);
        if(props.cleanGraphqlVariables) {
            // pass the full `values` so cleanGraphqlVariables can read transient
            // (derived) fields that the diff engine has stripped from `variables`
            // (e.g. the rigging date/time split of the wire DateTime fields).
            variables = props.cleanGraphqlVariables(variables,transientValues,values);
        }
        if (!variables) {
            toast('Nothing to save', {variant: 'error'});
            return;
        }
        toast('Saving '+props.label,{id:nextSnackbarKey()+'_SAVE'});
        if(props.saveGuard) {
            const res = await props.saveGuard(values,initialValues);
            if(!res) {
                return;
            }
        }
        if (!isNew && !values.__cloned) {
            props.save({values:variables as Type}).then((data) => {
                if (data.errors) {
                    data.errors.forEach((error) => {
                        toast(error.message,{variant:'error'});
                    });
                } else {
                    toast(props.label+' updated.',{id:nextSnackbarKey()+'_SAVED',variant:'success'});
                }
                hasCompleted();
                if(props.afterSave) {
                    props.afterSave({setTransient:setTransient});
                }
            }).catch((error) => {
                hasFailed();
                toast(error.message,{id:'CAMPAIGN_SAVED',variant:'error'});
            })
        } else {
            // The change document is a JSON object; narrow to strip id/auto_upgrade.
            const doc = variables as Record<string, unknown>;
            if ("id" in doc) {
                delete doc.id;
            }
            if ("auto_upgrade" in doc) {
                delete doc.auto_upgrade;
            }
            props.create({values:variables as Type}).then((data) => {
                toast(props.label+' created.',{variant:'success'});
                hasCompleted();
                if(props.afterSave) {
                    props.afterSave({setTransient:setTransient});
                }
                const url = props.getCreateRedirect({data:data as any,baseUrl:props.baseUrl,search:location.search});
                allowNextNavigation();
                navigate(url);
            }).catch((e) => {
                hasFailed();
                toast('Unable to create '+props.label,{variant:'error'});
            });
        }
    }, [isNew,props.save,props.create,props.saveGuard]);
    const formFail = useCallback(() => {
        toast('One or more field has failed validation, please correct any highlighted errors and try again',{variant:'error'});
    }, [isNew]);

    const [blankValues,setBlankValues] = useState(props.blankValues());
    useEffect(()=>{
        setBlankValues(props.blankValues);
    },[props.blankValues]);
    const diaForm = useDiaForm<Type,TransientType>({
        inputValues:isNew?blankValues:props.getValue(props.query),
        onSubmit: formSubmit,
        onFailure: formFail,
        transientValues:props.transientValues,
        relationships: props.relationships,
        validationSchema: props.validationSchema,
        isNew: isNew,
        loaded:!(props.query as any).loading, // meh
    });
    const {confirmation, allowNextNavigation} = useUnsavedChangesGuard({
        isDirty: diaForm.isDirty,
        save: () => diaForm.handleSubmit(),
        allowNavigation: props.allowRedirectWhen ? (next) => props.allowRedirectWhen(next.pathname, id) : undefined
    });

    return {form:diaForm,waiting:waiting,confirmation:confirmation};
}